"use client";

/**
 * Schedule Live Banner
 *
 * A slim, full-width, brand-colored bar pinned to the top of the public page. It surfaces
 * what's happening "now" at the event: a carousel cycling through every currently-live item
 * ("NOW: {title} · {location} · {time}" on one line on desktop, stacked over two lines on mobile),
 * "Up next · in X min" slides when nothing is live (the next item plus any starting within the next
 * hour), and a live countdown + "View schedule" CTA before the event ("Starts in {Dd : HHh : MMm :
 * SSs}"). In the live/next states the whole slide links to the schedule section (no separate button,
 * unlike the pre-event CTA). Once nothing is live and nothing is upcoming, the bar hides entirely.
 * The name truncates while the time stays pinned, so the time is never cut.
 *
 * Reads the once-fetched data from `SchedulePublicDataProvider` and ticks a local `useNow` so a
 * minute tick re-renders only this bar, not the page. Rendered only for public viewers when the
 * event has a time zone and the schedule has timed items; otherwise it renders nothing.
 *
 * The builder canvas reuses this same bar as a frozen preview (see `SchedulePreviewBanner`): pass
 * `previewData` to source the data from the store instead of the public context and bypass the
 * public-view gate. In that mode the carousel doesn't auto-advance and the countdown is static.
 */

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { useBuilderContext } from "../../_hooks/useBuilderContext";
import { useClientNow } from "../../_hooks/useNow";
import {
  CAROUSEL_ADVANCE_MS,
  SCHEDULE_NEXT_WINDOW_MINUTES,
  SCHEDULE_UPCOMING_SOON_MINUTES,
} from "../../_logic/constants";
import {
  computeLiveStatus,
  formatCountdown,
  formatScheduleTime,
  formatStartsIn,
  type ScheduleLiveStatus,
  type TimedScheduleItem,
} from "../../_logic/utils/schedule-utils";
import { ScheduleCountdown } from "../canvas/renderers/schedule/ScheduleCountdown";
import { scrollToAnchor } from "../../_logic/utils/scroll-utils";
import { usePublicViewContext } from "./PublicViewContext";
import { useSchedulePublicData, type SchedulePublicData } from "./SchedulePublicDataProvider";

/** Stable empty reference so the status memo isn't invalidated by a fresh `[]` each render. */
const EMPTY_TIMED_ITEMS: TimedScheduleItem[] = [];

/**
 * Responsive bar height for the live and next-up slides: a compact one-line bar on desktop
 * (`sm:h-12` = 48px) where the slide fits on a single line, and a two-line bar on mobile
 * (`h-14` = 56px) where the name and the location · time stack. The actual height is measured at
 * runtime and published to the navbar, so these stay the single source of truth without
 * hardcoding a matching number elsewhere.
 */
const BAR_HEIGHT_CLASS = "h-14 sm:h-12";

/**
 * Pre-event countdown bar: "Starts in" and "View schedule" share one row at 48px on every screen,
 * and wrap onto a second line only when the phone is too narrow to fit both.
 */
const COUNTDOWN_BAR_HEIGHT_CLASS = "min-h-12 py-1.5";

/**
 * Replace ASCII spaces with non-breaking spaces so a value (e.g. a time range like
 * "8:30 – 9:30 AM EST") never wraps or truncates mid-chunk — the whole time stays on one line.
 */
function keepTogether(text: string): string {
  return text.replace(/ /g, " ");
}

/**
 * One carousel slide, split across two lines so the schedule item name (not the time) is the
 * text that gets ellipsized when a slide is too long for the bar.
 */
type BannerSlide = {
  /** Stable key for animation remount + pager dedup. */
  key: string;
  /** Bold first line. The item name sits at its end, so it's the first text to be cut with "…". */
  primary: string;
  /** Second-line location; truncates before the time does. */
  location?: string;
  /** Second-line time; pinned and never truncated (kept on one line). */
  time?: string;
};

/** Build the carousel slides for a live status. Live items each get a slide; other states one. */
function buildSlides(status: ScheduleLiveStatus, timeZone: string): BannerSlide[] {
  switch (status.kind) {
    case "before":
      // Pre-event state is rendered separately as a live countdown + CTA (not a carousel slide),
      // so it contributes no slides here. See the `status.kind === "before"` branch in the render.
      return [];
    case "live": {
      const nowSlides = status.items.map((entry, i) => {
        const item = entry.item;
        return {
          key: `live-${i}-${item.title}`,
          primary: `NOW: ${item.title}`,
          location: item.location ?? undefined,
          time: keepTogether(formatScheduleTime(item.start_time, item.end_time, { timeZone, date: item.date })),
        };
      });
      // "NEXT" slides — items starting within the soon-window, appended after the live ones so the
      // carousel reads "what's on now" first, then "what's coming up".
      const nextSlides = status.upcoming.map(({ item: entry, startsInMinutes }, i) => {
        const item = entry.item;
        return {
          key: `next-${i}-${item.title}`,
          primary: `NEXT · in ${formatStartsIn(startsInMinutes)} · ${item.title}`,
          location: item.location ?? undefined,
          time: keepTogether(formatScheduleTime(item.start_time, item.end_time, { timeZone, date: item.date })),
        };
      });
      return [...nowSlides, ...nextSlides];
    }
    case "next": {
      // Nothing live: show the next item plus any later items within the window (the next hour).
      return status.upcoming.map(({ item: entry, startsInMinutes }, i) => {
        const item = entry.item;
        return {
          key: `next-${i}-${item.title}`,
          primary: `Up next · in ${formatStartsIn(startsInMinutes)} · ${item.title}`,
          location: item.location ?? undefined,
          time: keepTogether(formatScheduleTime(item.start_time, item.end_time, { timeZone, date: item.date })),
        };
      });
    }
    case "ended":
      // Nothing live and nothing upcoming: the banner is hidden by the render, so it has no slide.
      return [];
  }
}

/** Subset of public schedule data the bar reads — supplied directly in editor-preview mode. */
export type ScheduleBannerData = Pick<SchedulePublicData, "bannerEnabled" | "timedItems" | "loaded" | "scheduleHref">;

interface ScheduleLiveBannerProps {
  /**
   * Editor-preview data source (builder canvas). When provided, the bar reads its data from this
   * prop instead of the public `SchedulePublicDataProvider` context and bypasses the public-view
   * gate. Its presence is what puts the bar in preview mode: a frozen snapshot — the carousel does
   * not auto-advance and the pre-event countdown is rendered statically rather than ticking.
   */
  previewData?: ScheduleBannerData;
  /** Extra classes merged into the bar's root (e.g. `rounded-t-lg` to match the canvas page card). */
  rootClassName?: string;
}

/** Renders the sticky live "happening now" banner. */
export function ScheduleLiveBanner({
  previewData,
  rootClassName = "",
}: ScheduleLiveBannerProps = {}): React.ReactElement | null {
  // Hooks
  const { primaryColor, timeZone } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const contextData = useSchedulePublicData();
  const now = useClientNow();

  // Preview mode (builder canvas) is driven entirely by the presence of `previewData`: the bar then
  // reads that data, treats itself as visible without the public-view gate, and freezes its motion.
  const preview = previewData != null;
  const data = previewData ?? contextData;
  const visible = isPublic || preview;

  // States
  const [index, setIndex] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Refs — the sticky bar's root element (skeleton or full). Measured so the navbar offset tracks
  // the bar's real rendered height rather than a constant that can drift from it.
  const rootRef = useRef<HTMLDivElement>(null);

  // Derived values
  // `bannerEnabled` is decided server-side: it reflects the family's master live-banner switch plus
  // the in-page/site-wide policy. When false the bar renders nothing (not even a skeleton), so pages
  // that load schedule data only for their Schedule section don't flash a banner placeholder.
  const bannerEnabled = data?.bannerEnabled ?? false;
  const timedItems = data?.timedItems ?? EMPTY_TIMED_ITEMS;
  const loaded = data?.loaded ?? false;
  const scheduleHref = data?.scheduleHref ?? null;
  const status = useMemo<ScheduleLiveStatus>(
    () =>
      now == null
        ? { kind: "ended" }
        : computeLiveStatus(timedItems, now, SCHEDULE_UPCOMING_SOON_MINUTES, SCHEDULE_NEXT_WINDOW_MINUTES),
    [timedItems, now]
  );
  const slides = useMemo(() => (timeZone ? buildSlides(status, timeZone) : []), [status, timeZone]);
  // Signature of the current slide set (stable across plain minute ticks when the live items are
  // unchanged) — used to reset the carousel position only when the set actually changes.
  const slideKey = useMemo(() => slides.map((s) => s.key).join("|"), [slides]);
  const textColor = useMemo(() => getButtonTextColor(primaryColor), [primaryColor]);
  const shellStyle = useMemo(() => ({ backgroundColor: primaryColor, color: textColor }), [primaryColor, textColor]);

  // The event is over (or has no timed items) once the status resolves to "ended" — the banner
  // shows nothing in that case (no "Event ended" slide), so it's excluded from the present/live
  // flags below. Guarded by `loaded`/`now` so the loading skeleton still reserves height.
  const isEnded = now != null && loaded && status.kind === "ended";
  const showLive =
    visible && bannerEnabled && timeZone != null && now != null && loaded && timedItems.length > 0 && !isEnded;
  // True whenever the bar (skeleton or full) occupies the top of the viewport — mirrors the
  // render conditions below. Used to offset the sticky navbar so the two bars stack instead of
  // overlapping.
  const bannerPresent =
    visible && bannerEnabled && timeZone != null && (now == null || !loaded || (timedItems.length > 0 && !isEnded));
  const hasMultiple = slides.length > 1;
  const paused = interacting || hidden;

  // Handlers
  // Smooth-scroll to the schedule section. Shared by the pre-event "View schedule" CTA and the
  // live/next states (where the whole slide is a link, with no separate button). Cross-page links
  // (`/slug#anchor`) navigate normally; only same-page `#anchor` links are intercepted for an
  // offset scroll that clears the sticky chrome (this banner + the navbar stacked below it).
  const handleScheduleNavClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (!scheduleHref || !scheduleHref.startsWith("#")) return;
    e.preventDefault();
    const id = scheduleHref.slice(1);
    if (!document.getElementById(id)) return;
    scrollToAnchor(id);
  };

  // Effects — pause auto-advance when the tab is backgrounded
  useEffect(() => {
    const onVisibility = (): void => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Effects — reset to the first slide whenever the slide set changes (live items start/end, status
  // transition) so the shown slide and pager dots can't be stuck on a stale clamped index.
  useEffect(() => {
    setIndex(0);
  }, [slideKey]);

  // Effects — carousel auto-advance (gated: only when shown, multiple slides, not paused, and never
  // in the editor preview, which is a frozen snapshot)
  useEffect(() => {
    if (!showLive || !hasMultiple || paused || preview) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), CAROUSEL_ADVANCE_MS);
    return () => clearInterval(id);
  }, [showLive, hasMultiple, paused, preview, slides.length]);

  // Effects — offset native/`scrollIntoView` anchor-scroll targets by the full sticky chrome (this
  // bar PLUS the navbar stacked below it) so neither covers the section a `#hash` link scrolls to.
  // Reads the measured bar height published below as a CSS var, so it tracks the responsive bar
  // height. Scoped to the document root and cleared when the bar hides.
  useEffect(() => {
    // Skip in the editor preview: the canvas scrolls its own container, not the document, so a
    // document-level scroll-padding would be a surprising global side effect with no benefit here.
    if (!showLive || preview) return;
    const root = document.documentElement;
    const previous = root.style.scrollPaddingTop;
    // References both sticky-bar vars (each 0 when its bar is absent), so the padding self-adjusts to
    // whatever chrome is actually on screen — navbar height is published by NavbarRenderer.
    root.style.scrollPaddingTop = "calc(var(--lp-navbar-height, 0px) + var(--lp-live-banner-height, 0px))";
    return () => {
      root.style.scrollPaddingTop = previous;
    };
  }, [showLive, preview]);

  // Effects — publish the bar's *measured* height as a CSS variable while it's pinned so the sticky
  // navbar can offset its own `top` and stack flush below us instead of being painted over. A
  // ResizeObserver keeps the value exact across breakpoints and content changes, so the navbar never
  // gets clipped by a stale constant. Cleared when the bar hides. `showLive` is a dep so the observer
  // re-attaches to the new root when we swap the skeleton for the full bar.
  useEffect(() => {
    if (!bannerPresent) return;
    const el = rootRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = (): void => {
      const height = Math.round(el.getBoundingClientRect().height);
      root.style.setProperty("--lp-live-banner-height", `${height}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--lp-live-banner-height");
    };
  }, [bannerPresent, showLive]);

  // Render — reserve height with a skeleton while loading; render nothing once we know there's
  // no timed schedule, when this isn't a public, time-zoned page, or when the banner isn't
  // enabled for this page (master switch off, or off-section page with the site-wide toggle off).
  if (!visible || timeZone == null || !bannerEnabled) return null;

  if (now == null || !loaded) {
    return (
      <div
        ref={rootRef}
        className={`sticky top-0 z-50 flex w-full items-center justify-center px-4 ${BAR_HEIGHT_CLASS} ${rootClassName}`}
        style={shellStyle}
        aria-hidden="true"
      >
        <div className="h-3 w-40 animate-pulse rounded-full bg-white/30" />
      </div>
    );
  }

  if (timedItems.length === 0) return null;

  // Nothing is live and nothing is upcoming → hide the banner entirely (no "Event ended" slide).
  if (status.kind === "ended") return null;

  // Pre-event state — a live digital countdown to the first item plus an optional "View schedule"
  // CTA. Rendered as its own (non-carousel) layout; the bar self-sizes within the responsive height
  // and its measured height is published to the navbar like the other states.
  if (status.kind === "before") {
    return (
      <div
        ref={rootRef}
        role="region"
        aria-label="Live schedule"
        className={`sticky top-0 z-50 flex w-full items-center justify-center px-3 sm:px-4 ${COUNTDOWN_BAR_HEIGHT_CLASS} ${rootClassName}`}
        style={shellStyle}
      >
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 sm:gap-x-2.5">
          <div className="flex items-baseline gap-1.5 text-sm">
            <span className="font-medium opacity-90">Starts in</span>
            {/* Editor preview is a frozen snapshot: render the countdown statically instead of the
                second-by-second ticking ScheduleCountdown. `now` is non-null past the guard above. */}
            {preview ? (
              <span className="font-semibold tabular-nums tracking-wide">
                {status.firstStartMs - now > 0 ? formatCountdown(status.firstStartMs - now) : "Starting now"}
              </span>
            ) : (
              <ScheduleCountdown targetMs={status.firstStartMs} />
            )}
          </div>
          {scheduleHref && (
            <a
              href={scheduleHref}
              onClick={handleScheduleNavClick}
              className="inline-flex shrink-0 touch-manipulation items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-white/25 sm:px-3 sm:text-sm"
            >
              View schedule
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
    );
  }

  const currentSlide = slides[Math.min(index, slides.length - 1)];
  if (!currentSlide) return null;
  const hasSecondary = Boolean(currentSlide.location || currentSlide.time);
  const goPrev = (): void => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const goNext = (): void => setIndex((i) => (i + 1) % slides.length);

  // The slide's text block. Below, it's wrapped in a link to the schedule whenever a schedule anchor
  // exists — in the live/next states the whole slide is the tap target, with no separate button
  // (unlike the pre-event CTA).
  // Responsive layout: on mobile the bold name line stacks over a lighter location · time line; on
  // `sm+` everything sits on one line ("NOW: {title} · {location} · {time}"). In both, the name is
  // the only part that truncates with "…" (it has `min-w-0`/`truncate` and shrinks), while the time
  // is `whitespace-nowrap` + `shrink-0`, so it's pinned and always fully visible. aria-live is off
  // because the carousel auto-advances and would otherwise flood screen readers. `key={currentSlide.key}`
  // remounts the block per slide so the fade-up animation replays; `motion-reduce:animate-none` honors
  // reduced-motion.
  const slideInner = (
    <div
      key={currentSlide.key}
      className="flex w-full min-w-0 flex-col items-center gap-0.5 animate-slideUpAndFade motion-reduce:animate-none sm:flex-row sm:flex-nowrap sm:items-center sm:justify-center sm:gap-1.5"
      aria-live="off"
    >
      <p className="w-full truncate text-center text-sm font-semibold sm:w-auto sm:min-w-0">{currentSlide.primary}</p>
      {hasSecondary && (
        <>
          {/* Separator shown only on the one-line desktop layout, between name and meta. */}
          <span className="hidden shrink-0 text-sm font-medium opacity-90 sm:inline" aria-hidden="true">
            ·
          </span>
          <div className="flex w-full min-w-0 items-center justify-center gap-1 text-xs font-medium opacity-90 sm:w-auto sm:shrink-0 sm:text-sm">
            {currentSlide.location && <span className="min-w-0 truncate">{currentSlide.location}</span>}
            {currentSlide.location && currentSlide.time && (
              <span className="shrink-0" aria-hidden="true">
                ·
              </span>
            )}
            {currentSlide.time && <span className="shrink-0 whitespace-nowrap">{currentSlide.time}</span>}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label="Live schedule"
      className={`relative sticky top-0 z-50 flex w-full ${BAR_HEIGHT_CLASS} ${rootClassName}`}
      style={shellStyle}
      onPointerEnter={() => setInteracting(true)}
      onPointerLeave={() => setInteracting(false)}
      onTouchStart={() => setInteracting(true)}
      onTouchEnd={() => setInteracting(false)}
      onTouchCancel={() => setInteracting(false)}
      onFocusCapture={() => setInteracting(true)}
      onBlurCapture={() => setInteracting(false)}
    >
      {/* The content region stretches to the full bar height and centers its slide, so the text and
          the edge chevrons share the bar's true vertical center (the pager dots below are taken out
          of flow so they don't pull that center upward). Chevrons are absolutely positioned at the
          edges to keep a 44px touch target. */}
      <div className="relative mx-auto flex w-full max-w-5xl items-center justify-center px-12">
        {hasMultiple && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous"
            className="absolute left-1 top-1/2 flex h-11 w-11 -translate-y-1/2 touch-manipulation items-center justify-center rounded-full transition-colors hover:bg-white/15"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        {/* Tap target: in the live/next states the whole centered slide links to the schedule (no
            separate button). The edge chevrons are siblings, not nested in this link, so their
            carousel clicks stay independent. Falls back to a plain div when there's no schedule
            anchor to link to. */}
        {scheduleHref ? (
          <a
            href={scheduleHref}
            onClick={handleScheduleNavClick}
            className="flex w-full min-w-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded outline-offset-2 transition-opacity hover:opacity-90"
          >
            {slideInner}
          </a>
        ) : (
          <div className="flex w-full min-w-0 flex-col items-center justify-center gap-0.5">{slideInner}</div>
        )}

        {hasMultiple && (
          <button
            type="button"
            onClick={goNext}
            aria-label="Next"
            className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 touch-manipulation items-center justify-center rounded-full transition-colors hover:bg-white/15"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Pager dots are pinned to the bottom edge (out of flow) so they hold a constant position
          regardless of whether the current slide is one or two lines tall, and don't steal vertical
          space from the centered content above. */}
      {hasMultiple && (
        <div className="absolute inset-x-0 bottom-1 flex items-center justify-center gap-1" aria-hidden="true">
          {slides.map((_, i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-current transition-opacity"
              style={{ opacity: i === Math.min(index, slides.length - 1) ? 1 : 0.4 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
